import { BaseCrud } from '@/shared/models/cruds.models'
import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import IncidentsOriginsModalComponent
    from '@/modules/configurationModule/detail/incidents/incidents-origins/components/incidents-origins-modal/incidents-origins-modal.vue'
import IncidentsOriginsModal
    from '@/modules/configurationModule/detail/incidents/incidents-origins/components/incidents-origins-modal/incidents-origins-modal'
import {
    IncidentsOriginsGridState,
    initialIncidentsOriginsGridState
} from '@/modules/configurationModule/detail/incidents/incidents-origins/components/incidents-origins-grid/incidents-origins-grid.model'
import { incidentsOriginsLaboratoriesConfigurationServices } from '@/modules/configurationModule/detail/incidents/incidents-origins/services/incidents-origins-laboratories.service'
import {
    ConfigurationIncidentsOriginsLaboratoriesCreateRequest,
    ConfigurationIncidentsOriginsLaboratoriesPatchInBatchRequest,
    ConfigurationIncidentsOriginsLaboratoriesResponse
} from '@/modules/configurationModule/detail/incidents/incidents-origins/services/incidents-origins-laboratories.dto'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        IncidentsOriginsModalComponent
    }
})
export default class IncidentsOriginsGrid extends BaseCrudServerSideComponent<ConfigurationIncidentsOriginsLaboratoriesResponse> implements BaseCrud {
    @Ref() readonly incidentsOriginsModalWrapper!: any
    @Ref() readonly incidentsOriginsModal!: IncidentsOriginsModal

    state: IncidentsOriginsGridState
    constructor() {
        super()
        this.state = initialIncidentsOriginsGridState()
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    get canUpdate(): boolean {
        const numberRowSelected: number = this.state.selectedRows.length
        return numberRowSelected === 1
    }

    private _showModalUpdateAction(): void {
        this.showModal(this.$t('configuration.actions.update').toString(), this.state.selectedRows[0], this.incidentsOriginsModalWrapper)
    }

    private _showModalCreateAction(): void {
        this.showModal(this.$t('configuration.actions.add').toString(), null, this.incidentsOriginsModalWrapper)
    }

    private _onPressShortKeyUpdateButton(): void {
        this._showModalUpdateAction()
    }

    private _onClickUpdateButton(): void {
        this._showModalUpdateAction()
    }

    private _onClickCreateButton(): void {
        this._showModalCreateAction()
    }

    private _onPressShortKeyCreateButton(): void {
        this._showModalCreateAction()
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return params ? [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:RowNode | null = params?.node
                    const selectedRow: ConfigurationIncidentsOriginsLaboratoriesResponse = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update') as string, selectedRow, this.incidentsOriginsModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data?.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ] : []
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: ConfigurationIncidentsOriginsLaboratoriesResponse[]): Promise<void> {
        const filteredRowActiveState = selectionBatch?.filter((row: ConfigurationIncidentsOriginsLaboratoriesResponse) => row.active !== option)
        const updatedFilteredRowActiveState: ConfigurationIncidentsOriginsLaboratoriesPatchInBatchRequest[] = filteredRowActiveState?.map((row: ConfigurationIncidentsOriginsLaboratoriesResponse) => {
            return {
                id: row.id,
                active: option
            }
        })
        if (updatedFilteredRowActiveState?.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(incidentsOriginsLaboratoriesConfigurationServices.patchIncidentsOriginsLaboratory(this.state.labId, updatedFilteredRowActiveState), true)
            this.$_updateLoading(false)
            if (err) {
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async createOperation(row: ConfigurationIncidentsOriginsLaboratoriesCreateRequest): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(incidentsOriginsLaboratoriesConfigurationServices.createIncidentsOriginsLaboratory(this.state.labId, row), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        await this.incidentsOriginsModal.initializeIncidentOrigin(response)
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(rows: ConfigurationIncidentsOriginsLaboratoriesPatchInBatchRequest[]): Promise<void> {
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(incidentsOriginsLaboratoriesConfigurationServices.patchIncidentsOriginsLaboratory(this.state.labId, rows), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.incidentsOriginsModalWrapper.hide()
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async deleteOperation(rows: ConfigurationIncidentsOriginsLaboratoriesResponse[]): Promise<void> {
        if (rows.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this._deleteBatchOperation(rows)
            }
        }
    }

    private async _deleteBatchOperation(rows: ConfigurationIncidentsOriginsLaboratoriesResponse[]): Promise<void> {
        const rowsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: rows.map((incidentOrigin: ConfigurationIncidentsOriginsLaboratoriesResponse) => Number(incidentOrigin?.id))
        }
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(incidentsOriginsLaboratoriesConfigurationServices.deleteIncidentsOriginsLaboratory(this.state.labId, rowsToDelete), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        if (rows.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: rows?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }
}
